import { AuditStatus } from '@arhs-fleetback/types/entities/audits';
import { equals as isEqual } from 'ramda';
import { matchPath } from 'react-router';
import { createSelector, createSelectorCreator, lruMemoize } from 'reselect';

import { hasAccess } from 'components/common/utils/hasAccess';
import { PATH } from 'constants/RoutesConstants';
import { AUDITOR, AUDIT_MANAGER, BACK_OFFICE, LEADERSHIP_ROLES } from 'constants/UserConstants';
import type { MayBe } from 'modules/global/typing/common/common';
import { getCurrentUserRoles, getCurrentUserId, getCurrentUserIsReadOnly } from 'modules/users/selectors/currentUser';
import { UserTypeEnum } from 'modules/users/types/users.enum';

import type { RootState } from 'reducers/index';
import { getCurrentLocation } from 'selectors/router';
import { getUsers } from 'selectors/userSelectors';
import { getProps } from 'utils/selectors';

import { getId, getDataById } from './global';
import { EMPTY_ASSIGNMENT } from '../reducer/ui/details/assignment/data';
import type { AuditResponse } from '../types/resources/questions/responses/audit-response';
import type { AuditSection } from '../types/resources/sections/audit-section';
import type { StoredAudit } from '../types/stored-audit';
import { hasOriginIntervention, isDeletableAudit, isOriginInterventionLinkable } from '../utils';

const getRoot = (state: RootState) => state;

const getState = createSelector(getRoot, root => root.audit.ui.details);

const getSelectId = getId;

const getAllowedRoles = () => [AUDITOR, AUDIT_MANAGER, BACK_OFFICE, ...LEADERSHIP_ROLES];

// The selected audit is derived from the URL (no dedicated reducer): the detail route is the single
// source of truth for which audit is open.
export const getSelectedId = createSelector(getCurrentLocation, pathname => {
  const match = matchPath(PATH.AUDITS_DETAILED.path, pathname);
  return match?.params?.auditId;
});

export const getSelectedAudit = createSelector(getRoot, getSelectedId, getDataById);

const extractMarkers = (unformattedAudit: MayBe<StoredAudit>): MayBe<StoredAudit> => {
  if (typeof unformattedAudit !== 'object' || !unformattedAudit) {
    return unformattedAudit;
  }

  const { sections, ...audit } = unformattedAudit;
  return {
    ...audit,
    sections: (sections ?? []).reduce<AuditSection[]>((acc, section) => {
      acc.push({
        ...section,
        questions: section.questions.map(question => {
          const { markers: _markers, ...rest } = question as AuditResponse & { markers?: unknown };
          return rest as AuditResponse;
        }),
      });
      return acc;
    }, []),
  } as StoredAudit;
};

const customSelector = createSelectorCreator(lruMemoize, (auditA: MayBe<StoredAudit>, auditB: MayBe<StoredAudit>) =>
  isEqual(extractMarkers(auditA), extractMarkers(auditB)));

export const getInitialFormValues = customSelector(getSelectedAudit, audit => audit);

const getStatus = createSelector(getSelectedAudit, (audit: MayBe<StoredAudit>) => audit?.status);

const isLoading = createSelector(getState, state => state.loading.fetch);

export const isEditing = createSelector(getState, state => state.isEditing);

export const userCanUpdate = createSelector(
  getAllowedRoles,
  getCurrentUserRoles,
  getCurrentUserIsReadOnly,
  (allowedRoles, roles: string[], isReadOnly) => !isReadOnly && hasAccess(roles, allowedRoles)
);

export const canEdit = createSelector(
  userCanUpdate,
  getStatus,
  (canUpdate, status) => canUpdate && status === AuditStatus.DRAFT
);

export const canShare = createSelector(
  getStatus,
  status => status !== AuditStatus.DRAFT && status !== AuditStatus.CLOSED && status !== AuditStatus.TO_START
);

export const canDuplicated = createSelector(
  userCanUpdate,
  getStatus,
  (canUpdate, status) => canUpdate && status !== AuditStatus.CLOSED && status !== AuditStatus.TO_START
);

export const isToStart = createSelector(getStatus, status => status === AuditStatus.TO_START);

export const canStart = createSelector(
  userCanUpdate,
  getStatus,
  (canUpdate, status) => canUpdate && status === AuditStatus.TO_START
);

export const isFromOriginIntervention = createSelector(
  getSelectedAudit,
  (audit: MayBe<StoredAudit>): boolean => hasOriginIntervention(audit)
);

export const isOriginLinkClickable = createSelector(
  getDataById,
  (audit: MayBe<StoredAudit>) => isOriginInterventionLinkable(audit?.originIntervention)
);

export const isSystemAuthor = createSelector(
  getDataById,
  (audit: MayBe<StoredAudit>) => audit?.author?.type === UserTypeEnum.SYSTEM
);

export const canEditStartDate = createSelector(
  userCanUpdate,
  getDataById,
  (canUpdate, audit: MayBe<StoredAudit>) =>
    canUpdate &&
    !audit?.originIntervention &&
    audit?.status !== AuditStatus.OPEN &&
    audit?.status !== AuditStatus.CLOSED
);

export const canSubmit = createSelector(
  userCanUpdate,
  getStatus,
  (canUpdate, status) => canUpdate && status !== AuditStatus.OPEN
);

export const canClose = createSelector(
  userCanUpdate,
  getStatus,
  (canUpdate, status) => canUpdate && status === AuditStatus.OPEN
);

export const canReopen = createSelector(
  userCanUpdate,
  getStatus,
  (canUpdate, status) => canUpdate && status === AuditStatus.CLOSED
);

export const canDelete = createSelector(
  getSelectedAudit,
  getCurrentUserId,
  getCurrentUserRoles,
  getCurrentUserIsReadOnly,
  (audit: MayBe<StoredAudit>, userId: string, roles: string[], isReadOnly) =>
    !isReadOnly && isDeletableAudit(audit, userId, roles)
);

export const isReady = createSelector(
  getSelectedAudit,
  isLoading,
  (audit: MayBe<StoredAudit>, isProcessing) => !isProcessing && audit
);

export const getSearchSelectId = createSelector(getSelectId, selectId => `${selectId}-search`);

const getSearchUsersSelect = createSelector(
  getState,
  getSearchSelectId,
  (state, searchSelectId) => state.userSelect[searchSelectId]?.userIds
);

const getSearchLinks = createSelector(
  getState,
  getSearchSelectId,
  (state, searchId) => state.userSelect[searchId]?.links ?? []
);

const getSearchPage = createSelector(
  getState,
  getSearchSelectId,
  (state, searchId) => state.userSelect[searchId]?.page ?? {}
);

export const isSearching = createSelector(getSearchUsersSelect, userIds => userIds?.length > 0);

export const getUsersSelect = createSelector(
  getState,
  getUsers,
  getSelectId,
  getSearchUsersSelect,
  (state, users, selectId, searchUserIds) => {
    let userIds: string[] = (searchUserIds?.length > 0 ? searchUserIds : state.userSelect[selectId]?.userIds) ?? [];
    // Filter out duplicates
    userIds = userIds.filter((userId, index, array) => array.findIndex(id => id === userId) === index);
    return userIds.map(userId => users.content[userId]);
  }
);

export const getUsersLinks = createSelector(getState, getSelectId, getSearchLinks, (state, selectId, searchLinks) => {
  const links = searchLinks?.length > 0 ? searchLinks : state.userSelect[selectId]?.links;
  return links?.reduce(
    (acc: Record<string, { rel: string }>, link: { rel: string }) => ({
      ...acc,
      [link.rel]: link,
    }),
    {}
  );
});

export const getUsersPage = createSelector(getState, getSelectId, getSearchPage, (state, selectId, searchPage) =>
  (Object.keys(searchPage).length > 0 ? searchPage : state.userSelect[selectId]?.page));

const getLoading = createSelector(getState, state => state.loading);

export const isLoadingMarker = createSelector(getLoading, loading => loading.marker);
export const isUpdatingMarker = createSelector(getLoading, loading => loading.updatingMarker);

export const getSelectedMarkerId = createSelector(getState, state => state.selectedMarkerId);

export const getSections = createSelector(getSelectedAudit, (audit: MayBe<StoredAudit>) => audit?.sections);

const getAssignmentState = createSelector(getState, state => state.assignment);

const getAssignmentByAuditId = createSelector(getAssignmentState, assignment => assignment.data);

export const getAuditAssignment = createSelector(
  getAssignmentByAuditId,
  getProps<MayBe<string>>,
  (assignmentByAuditId, auditId) => (auditId ? assignmentByAuditId[auditId] : undefined) ?? EMPTY_ASSIGNMENT
);

export const isUpdatingAssignment = createSelector(getAssignmentState, assignment => assignment.loading);

const getStartDateState = createSelector(getState, state => state.startDate);

const getStartDateByAuditId = createSelector(getStartDateState, startDate => startDate.data);

export const getAuditStartDate = createSelector(
  getStartDateByAuditId,
  getProps<MayBe<string>>,
  (startDateByAuditId, auditId) => (auditId ? startDateByAuditId[auditId] : null) ?? null
);

export const isUpdatingStartDate = createSelector(getStartDateState, startDate => startDate.loading);

export const isDeleting = createSelector(getLoading, loading => loading.delete);

export const isPatchingStatus = createSelector(getLoading, loading => loading.status);

export const isStarting = createSelector(getLoading, loading => loading.start);

const getShareState = createSelector(getState, state => state.share);

const getSendState = createSelector(getShareState, share => share.send);

const getUrlState = createSelector(getShareState, share => share.url);

export const isOpenShareModalLoading = createSelector(getShareState, share => share.loading);

export const getSharedAuditId = createSelector(getShareState, share => share.auditId);

export const getShareSource = createSelector(getShareState, share => share.source);

export const isOpenShareModal = createSelector(getShareState, isOpenShareModalLoading, ({ open }, loading) => !loading && open);

export const getShareUrl = createSelector(getUrlState, url => url.url);

export const isLoadingUrl = createSelector(getUrlState, url => url.isLoading);

export const isLoadingSend = createSelector(getSendState, send => send.isLoading);

export const isPrintingCustomerView = createSelector(getLoading, loading => loading.printCustomerView);

export const getSelectedAuditHasArchivedMedias = createSelector(
  getSelectedAudit,
  (audit: MayBe<StoredAudit>) => audit?.hasArchivedMedias ?? false
);

export const getSelectedAuditArchivedMediasCount = createSelector(
  getSelectedAudit,
  ({ archivedMediaCount }) => archivedMediaCount ?? 0
);

export const getSelectedAuditHasRestoringMediasFlag = createSelector(
  getSelectedAudit,
  (audit: MayBe<StoredAudit>) => audit?.hasRestoringMedias ?? false
);
